import React, { useState } from "react";
import { Trash2, ChevronDown, ChevronUp, Highlighter } from "lucide-react";
import { useHighlights } from "../../contexts/HighlightsContext";



const formatTimestamp = (seconds, sessionStart) => {
  if (!Number.isFinite(seconds)) return "—";
  // Absolute wall-clock time when we know when the session started.
  // Same convention as the transcript view and navigation box.
  if (sessionStart) {
    const startDate = new Date(sessionStart);
    if (!Number.isNaN(startDate.getTime())) {
      const wallClock = new Date(startDate.getTime() + seconds * 1000);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(wallClock.getUTCHours())}:${pad(wallClock.getUTCMinutes())}:${pad(wallClock.getUTCSeconds())}`;
    }
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

/**
 * Sidebar-style overview of every highlight in the current transcription.
 * Click a row → jumps audio to the highlight's start. Trash icon → delete.
 * Pure presentation; all data comes from the HighlightsContext provider
 * that wraps SessionView.
 */
const HighlightsPanel = ({ onJumpTo, sessionStart }) => {
  const { highlights, isLoading, error, requestDeleteHighlight } =
    useHighlights();
  const [isOpen, setIsOpen] = useState(true);

  const handleDelete = (event, id) => {
    event.stopPropagation();
    requestDeleteHighlight(id);
  };

  const hasItems = highlights.length > 0;
  const expanded = isOpen && hasItems;

  return (
    <div className="flex flex-col rounded-2xl border border-outlineVariant bg-surface">
      <button
        type="button"
        onClick={() => {
          if (!hasItems) return;
          setIsOpen((v) => !v);
        }}
        className="flex items-center justify-between gap-2 px-3 py-1 text-sm font-medium text-onSurface"
        aria-expanded={expanded}
        disabled={!hasItems}
      >
        <span className="flex items-center gap-2">
          <Highlighter className="h-4 w-4" />
          Hervorhebungen
          <span className="rounded-full bg-secondaryContainer px-2 py-0.5 text-xs text-onSecondaryContainer">
            {highlights.length}
          </span>
        </span>
        {hasItems &&
          (expanded ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          ))}
      </button>

      {expanded && (
        <div className="max-h-[60vh] overflow-y-auto border-t border-outlineVariant">
          {isLoading && (
            <div className="px-3 py-2 text-xs text-onSurface/70">Wird geladen…</div>
          )}
          {error && !isLoading && (
            <div className="px-3 py-2 text-xs text-error">{error}</div>
          )}
          <ul className="divide-y divide-outlineVariant">
            {highlights.map((h) => (
              <li
                key={h.id}
                role="button"
                tabIndex={0}
                onClick={() => onJumpTo?.(h)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onJumpTo?.(h);
                  }
                }}
                className="group flex cursor-pointer items-start gap-2 px-3 py-2 transition hover:bg-secondaryContainer/40"
              >
                <span
                  className="mt-1 inline-block h-3 w-3 flex-shrink-0 rounded-full ring-1 ring-gray-300"
                  style={{ backgroundColor: h.color }}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-onSurface/60">
                    {formatTimestamp(h.start_offset, sessionStart)}
                  </div>
                  <div className="truncate text-sm text-onSurface">
                    {h.snippet || <span className="italic text-onSurface/60">(kein Text)</span>}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => handleDelete(e, h.id)}
                  className="flex-shrink-0 rounded-full p-1.5 text-error transition hover:bg-error/10"
                  aria-label="Hervorhebung löschen"
                  title="Löschen"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default HighlightsPanel;
